import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError, AggregateDomain } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type AnalyticsRow = {
  domainId: string;
  domainName: string;
  periodId: string;
  orgUnitId: string;
  dataElementId: string;
  value: number;
  sourceType: "tracker" | "submitted-aggregate" | "hybrid";
};

type ComparisonRow = {
  domainId: string;
  periodId: string;
  orgUnitId: string;
  trackerValue: number;
  submittedValue: number;
  variance: number;
};

type RollupFact = {
  id: string;
  domainId: string;
  dataElementId: string;
  periodId: string;
  orgUnitId: string;
  value: number;
  runId?: string;
  recomputedAt?: string;
};

type RollupRun = {
  id: string;
  domainId: string;
  mode: "incremental" | "full-rebuild";
  status: "running" | "completed" | "failed";
  startedAt: string;
  completedAt?: string;
  affectedFactCount: number;
  producedRollupCount: number;
};

const SOURCE_PILL: Record<AnalyticsRow["sourceType"], string> = {
  tracker: "pill-info",
  "submitted-aggregate": "pill-ok",
  hybrid: "pill-warn"
};

export default function AdminAnalytics() {
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [comparisons, setComparisons] = useState<ComparisonRow[]>([]);
  const [domains, setDomains] = useState<AggregateDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Filters
  const [domainId, setDomainId] = useState("");
  const [dataElementId, setDataElementId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [orgUnitId, setOrgUnitId] = useState("");
  const [sourceType, setSourceType] = useState("");

  // Lineage drawer state
  const [lineageRow, setLineageRow] = useState<AnalyticsRow | null>(null);
  const [lineageFacts, setLineageFacts] = useState<RollupFact[]>([]);
  const [lineageRuns, setLineageRuns] = useState<RollupRun[]>([]);
  const [lineageLoading, setLineageLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [analytics, doms] = await Promise.all([
        api.aggregateAnalytics(),
        api.aggregateDomains()
      ]);
      setRows(analytics.rows ?? []);
      setComparisons(analytics.comparisons ?? []);
      setDomains(doms.domains ?? []);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (domainId && r.domainId !== domainId) return false;
      if (dataElementId && !r.dataElementId.toLowerCase().includes(dataElementId.toLowerCase())) return false;
      if (periodId && !r.periodId.toLowerCase().includes(periodId.toLowerCase())) return false;
      if (orgUnitId && !r.orgUnitId.toLowerCase().includes(orgUnitId.toLowerCase())) return false;
      if (sourceType && r.sourceType !== sourceType) return false;
      return true;
    });
  }, [rows, domainId, dataElementId, periodId, orgUnitId, sourceType]);

  const totalValue = useMemo(() => filtered.reduce((sum, r) => sum + (Number.isFinite(r.value) ? r.value : 0), 0), [filtered]);

  const filteredComparisons = useMemo(
    () => comparisons.filter((c) => !domainId || c.domainId === domainId),
    [comparisons, domainId]
  );

  const tRows = useTableState(filtered as unknown as Array<Record<string, unknown>>, {
    searchFields: ["domainName", "domainId", "dataElementId", "periodId", "orgUnitId", "sourceType"],
    initialPageSize: 25
  });
  const tCmp = useTableState(filteredComparisons as unknown as Array<Record<string, unknown>>, {
    searchFields: ["domainId", "periodId", "orgUnitId"],
    initialPageSize: 25
  });

  const domainName = (id: string) => domains.find((d) => d.id === id)?.name ?? id;

  const onOpenLineage = async (row: AnalyticsRow) => {
    setLineageRow(row);
    setLineageFacts([]);
    setLineageRuns([]);
    setLineageLoading(true);
    try {
      const r = await api.request<{ runs: RollupRun[]; facts: RollupFact[] }>(
        `/api/submitted-aggregate/rollups?domainId=${encodeURIComponent(row.domainId)}`
      );
      const facts = (r.facts ?? []).filter((f) =>
        f.dataElementId === row.dataElementId &&
        f.periodId === row.periodId &&
        f.orgUnitId === row.orgUnitId
      );
      const runIds = new Set(facts.map((f) => f.runId).filter((id): id is string => Boolean(id)));
      setLineageFacts(facts);
      setLineageRuns((r.runs ?? []).filter((run) => runIds.has(run.id)));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLineageLoading(false);
    }
  };

  const onExport = async () => {
    setBusy(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (domainId) params.set("domainId", domainId);
      if (dataElementId) params.set("dataElementId", dataElementId);
      if (periodId) params.set("periodId", periodId);
      if (orgUnitId) params.set("orgUnitId", orgUnitId);
      if (sourceType) params.set("sourceType", sourceType);
      const qs = params.toString();
      await api.downloadAggregateAnalyticsCsv(qs);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Analytics explorer</h1>
          <p>Filter rollup analytics across aggregate domains and drill down into lineage.</p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
          <button type="button" className="btn btn-primary" onClick={() => void onExport()} disabled={busy || filtered.length === 0}>
            {busy ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}

      <section className="section">
        <div className="section-head"><h2>Filters</h2></div>
        <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <label>
            <span>Domain</span>
            <select value={domainId} onChange={(e) => setDomainId(e.target.value)}>
              <option value="">All domains</option>
              {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label>
            <span>Data element</span>
            <input value={dataElementId} onChange={(e) => setDataElementId(e.target.value)} placeholder="contains…" />
          </label>
          <label>
            <span>Period</span>
            <input value={periodId} onChange={(e) => setPeriodId(e.target.value)} placeholder="contains…" />
          </label>
          <label>
            <span>Org unit</span>
            <input value={orgUnitId} onChange={(e) => setOrgUnitId(e.target.value)} placeholder="contains…" />
          </label>
          <label>
            <span>Source</span>
            <select value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
              <option value="">Any source</option>
              <option value="tracker">Tracker</option>
              <option value="submitted-aggregate">Submitted aggregate</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </label>
          {(domainId || dataElementId || periodId || orgUnitId || sourceType) && (
            <button
              type="button"
              className="btn"
              style={{ alignSelf: "end" }}
              onClick={() => { setDomainId(""); setDataElementId(""); setPeriodId(""); setOrgUnitId(""); setSourceType(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Rollup analytics ({filtered.length} of {rows.length})</h2>
          <span className="soft">Sum of values: <strong>{totalValue.toLocaleString()}</strong></span>
        </div>
        {loading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={rows.length === 0 ? "No rollup facts yet" : "No rows match these filters"}
            description={rows.length === 0
              ? <>Run an aggregate rollup from <Link to="/admin/aggregate-domains">Aggregate Domain Studio</Link> to populate analytics.</>
              : "Adjust the filters above to see more rows."}
          />
        ) : (
          <div>
            <TableToolbar
              search={tRows.search} setSearch={tRows.setSearch}
              page={tRows.page} setPage={tRows.setPage}
              pageSize={tRows.pageSize} setPageSize={tRows.setPageSize}
              totalPages={tRows.totalPages} totalRows={tRows.totalRows}
              hideExport
              searchPlaceholder="Search data element / period / org unit…"
            />
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Data element</th>
                    <th>Period</th>
                    <th>Org unit</th>
                    <th>Value</th>
                    <th>Source</th>
                    <th>Lineage</th>
                  </tr>
                </thead>
                <tbody>
                  {(tRows.paged as unknown as AnalyticsRow[]).map((r, i) => (
                    <tr key={`${r.domainId}-${r.dataElementId}-${r.periodId}-${r.orgUnitId}-${i}`}>
                      <td><strong>{r.domainName}</strong><div className="soft" style={{ fontSize: 12 }}>{r.domainId}</div></td>
                      <td><code>{r.dataElementId}</code></td>
                      <td>{r.periodId}</td>
                      <td>{r.orgUnitId}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.value.toLocaleString()}</td>
                      <td><span className={`pill ${SOURCE_PILL[r.sourceType]}`}>{r.sourceType}</span></td>
                      <td><button type="button" className="linkbtn" onClick={() => void onOpenLineage(r)}>Inspect</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {comparisons.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>Tracker vs submitted variance</h2>
            <Link className="linkbtn" to="/admin/reconciliation">Reconciliation & integrity</Link>
          </div>
          <TableToolbar
            search={tCmp.search} setSearch={tCmp.setSearch}
            page={tCmp.page} setPage={tCmp.setPage}
            pageSize={tCmp.pageSize} setPageSize={tCmp.setPageSize}
            totalPages={tCmp.totalPages} totalRows={tCmp.totalRows}
            hideExport
            searchPlaceholder="Search domain / period / org unit…"
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Period</th>
                  <th>Org unit</th>
                  <th>Tracker</th>
                  <th>Submitted</th>
                  <th>Variance</th>
                </tr>
              </thead>
              <tbody>
                {(tCmp.paged as unknown as ComparisonRow[]).map((c, i) => (
                  <tr key={`${c.domainId}-${i}`}>
                    <td>{domainName(c.domainId)}</td>
                    <td>{c.periodId}</td>
                    <td>{c.orgUnitId}</td>
                    <td style={{ textAlign: "right" }}>{c.trackerValue.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{c.submittedValue.toLocaleString()}</td>
                    <td style={{ textAlign: "right", color: c.variance !== 0 ? "#b91c1c" : undefined }}>{c.variance.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Drawer
        open={Boolean(lineageRow)}
        title={lineageRow ? `Lineage · ${lineageRow.dataElementId}` : ""}
        subtitle={lineageRow ? `${lineageRow.domainName} · ${lineageRow.periodId} · ${lineageRow.orgUnitId}` : undefined}
        onClose={() => setLineageRow(null)}
        width={640}
        footer={<button type="button" className="btn" onClick={() => setLineageRow(null)}>Close</button>}
      >
        {lineageLoading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : (
          <>
            <h3>Rollup facts ({lineageFacts.length})</h3>
            {lineageFacts.length === 0 ? (
              <p className="soft">No rollup facts found at this coordinate.</p>
            ) : (
              <table className="data-table">
                <thead><tr><th>Fact ID</th><th>Value</th><th>Run</th><th>Recomputed</th></tr></thead>
                <tbody>
                  {lineageFacts.map((f) => (
                    <tr key={f.id}>
                      <td><code>{f.id.slice(0, 14)}…</code></td>
                      <td style={{ textAlign: "right" }}>{f.value.toLocaleString()}</td>
                      <td>{f.runId ? <code>{f.runId.slice(0, 12)}…</code> : "—"}</td>
                      <td className="soft">{f.recomputedAt ? new Date(f.recomputedAt).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3 style={{ marginTop: 16 }}>Producing runs ({lineageRuns.length})</h3>
            {lineageRuns.length === 0 ? (
              <p className="soft">No producing runs recorded for these facts.</p>
            ) : (
              <table className="data-table">
                <thead><tr><th>Run</th><th>Mode</th><th>Status</th><th>Started</th><th>Affected/Produced</th></tr></thead>
                <tbody>
                  {lineageRuns.map((run) => (
                    <tr key={run.id}>
                      <td><code>{run.id.slice(0, 12)}…</code></td>
                      <td>{run.mode}</td>
                      <td>{run.status}</td>
                      <td className="soft">{new Date(run.startedAt).toLocaleString()}</td>
                      <td>{run.affectedFactCount} / {run.producedRollupCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}
